<template>
        <button 
            class="base-card" 
            :class="[cardData.getClass()]"
            type="button"
            tabindex="0"
            @focus="onFocus(true)"
            @blur="onFocus(false)"
            @click="onItemSelected"
        >
            <component 
                :is="components[props.componentName]" 
                :card-data="cardData"
                :enable-program-info="enableProgramInfo"
                @program-info="onProgramInfo"
            />
        </button>
</template>

<script setup lang="ts">
    import { type PropType, type Ref, ref } from "vue";
    import { type CardData, ComponentName } from "./types";
    import Channel from "./components/Channel/Channel.vue";
    import Movie from "./components/Movie/Movie.vue";
    import TabsGenres from "./components/TabsGenres/TabsGenres.vue";
    import Program from "./components/Program/Program.vue";
    import ShowAll from "./components/ShowAll/ShowAll.vue";
    import Plus from "./components/Plus/Plus.vue";
    import Credit from "./components/Credit/Credit.vue";
    import Seasons from "./components/Seasons/Seasons.vue";
    import Episode from "./components/Episode/Episode.vue";
    import ChannelLive from "./components/ChannelLive/ChannelLive.vue";
    import FiltersVod from "./components/FiltersVod/FiltersVod.vue";
    import Mixed from "./components/Mixed/Mixed.vue";
    import type { IProgramInfo } from "@/composables/useProgramInfo/types";


    const components: Record<ComponentName, ReturnType<typeof defineComponent>> = {
        [ComponentName.CHANNEL]: Channel,
        [ComponentName.MOVIE]: Movie,
        [ComponentName.TABS_GENRES]: TabsGenres,
        [ComponentName.PROGRAM]: Program,
        [ComponentName.SEASONS]: Seasons,
        [ComponentName.SHOW_ALL]: ShowAll,
        [ComponentName.PLUS]: Plus,
        [ComponentName.CHANNEL_LIVE]: ChannelLive,
        [ComponentName.CREDIT]: Credit,
        [ComponentName.EPISODE]: Episode,
        [ComponentName.MIXED]: Mixed,
        [ComponentName.FILTERS_VOD]: FiltersVod,
    };

    const props = defineProps({
        cardData: {
            type: Object as PropType<CardData>,
            required: true
        },
        index: {
            type: Number,
            required: true,
        },
        componentName: {
            type: String as PropType<ComponentName>,
            default: '',
            required: false,
        },
        /**
         * The flag to indicate if the program info should be enabled
         */
         enableProgramInfo: {
            type: Boolean,
            required: false,
            default: false
        },
    });

    const emit = defineEmits(["index-focused", "program-info", "item-selected", "card-focused"]);
    const programInfo: Ref<IProgramInfo | null> = ref(null);

    const onProgramInfo = (event: IProgramInfo) => {
        programInfo.value = event;
    };

    const onFocus = (focus: boolean) => {
        if (focus) {
            emit("index-focused", props.index);
            emit("card-focused", props.cardData);
            
            if(props.enableProgramInfo && programInfo.value) {
                emit("program-info", programInfo.value);
            }
        }
    };

    const onItemSelected = () => {
        emit("item-selected", props.cardData);
    };
</script>

<style lang="scss">
    @import url("styles.scss");
</style>